import {
  ChangeDetectionStrategy,
  Component,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { firstValueFrom } from 'rxjs';
import {
  NotificationService,
  SelectOption,
  SkeletonLoaderComponent,
  StenaAffirmOrDismissDialogComponent,
  StenaAffirmOrDismissDialogData,
  StenaButtonComponent,
  StenaSelectComponent,
} from '@app-monorepo/shared/ui/stena-components';
import { PeriodsApiService } from '../../../services/periods-api.service';
import {
  CurrentOpenPeriod,
  Month,
  PeriodMode,
} from '../../../models/period.model';

// MonthId is a YYYYMM integer (e.g. 202607 → "2026-07"). Display-only —
// the numeric value is what gets sent to the API.
function formatMonthId(monthId: number): string {
  const year = Math.floor(monthId / 100);
  const month = monthId % 100;
  return `${year}-${String(month).padStart(2, '0')}`;
}

@Component({
  standalone: true,
  selector: 'app-admin-months-page',
  templateUrl: './admin-months-page.component.html',
  styleUrl: './admin-months-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    SkeletonLoaderComponent,
    StenaButtonComponent,
    StenaSelectComponent,
  ],
})
export class AdminMonthsPageComponent implements OnInit {
  private readonly api = inject(PeriodsApiService);
  private readonly notify = inject(NotificationService);
  private readonly fb = inject(FormBuilder);
  private readonly dialog = inject(MatDialog);

  readonly months = signal<Month[]>([]);
  readonly loading = signal(false);
  readonly saving = signal(false);
  readonly startMonthId = signal<number | null>(null);
  readonly endMonthId = signal<number | null>(null);

  readonly form = this.fb.group({
    mode: this.fb.control<PeriodMode>('actual', { nonNullable: true }),
    startMonthId: this.fb.control<number | null>(null),
    endMonthId: this.fb.control<number | null>(null),
  });

  readonly modeOptions: SelectOption<PeriodMode>[] = [
    { displayName: 'Actual', value: 'actual' },
    { displayName: 'Budget', value: 'budget' },
  ];

  readonly monthSelectOptions = computed<SelectOption<number>[]>(() =>
    this.months()
      .filter((m) => !m.isFiscalYearMarker)
      .map((m) => ({
        displayName: formatMonthId(m.monthId),
        value: m.monthId,
      }))
  );

  readonly currentPeriodLabel = computed(() => {
    const start = this.startMonthId();
    const end = this.endMonthId();
    if (!start || !end) return 'No period currently open';
    return `Current open period is from ${formatMonthId(start)} to ${formatMonthId(end)}`;
  });

  constructor() {
    this.form.controls.mode.valueChanges.subscribe((mode) =>
      this.loadCurrent(mode)
    );
    this.form.controls.startMonthId.valueChanges.subscribe((v) =>
      this.startMonthId.set(v)
    );
    this.form.controls.endMonthId.valueChanges.subscribe((v) =>
      this.endMonthId.set(v)
    );
  }

  ngOnInit(): void {
    this.loadMonths();
    this.loadCurrent(this.form.controls.mode.value);
  }

  onSave(): void {
    const { mode, startMonthId, endMonthId } = this.form.getRawValue();
    if (startMonthId === null || endMonthId === null) {
      this.notify.warning('Select both start and end month');
      return;
    }
    if (startMonthId > endMonthId) {
      this.notify.warning('Start month cannot be greater than end month');
      return;
    }
    this.saving.set(true);
    this.api.updateOpenPeriod({ startMonthId, endMonthId, mode }).subscribe({
      next: (result) => {
        this.applyCurrent(result);
        this.notify.success('Open period updated');
        this.saving.set(false);
      },
      error: (err) => {
        this.notify.error(err?.error?.message ?? 'Failed to update period');
        this.saving.set(false);
      },
    });
  }

  async onCreateFiscalYear(): Promise<void> {
    const data: StenaAffirmOrDismissDialogData = {
      title: 'Create next fiscal year',
      description:
        'This will add 12 new month rows for the next fiscal year. Continue?',
      affirmButtonText: 'Create',
      dismissButtonText: 'Cancel',
    };
    const ref = this.dialog.open(StenaAffirmOrDismissDialogComponent, { data });
    const confirmed = await firstValueFrom(ref.afterClosed());
    if (!confirmed) return;

    this.saving.set(true);
    this.api.createFiscalYear().subscribe({
      next: (result) => {
        this.notify.success(`Created ${result.createdMonthIds.length} rows`);
        this.loadMonths();
        this.saving.set(false);
      },
      error: (err) => {
        this.notify.error(
          err?.error?.message ?? 'Failed to create fiscal year'
        );
        this.saving.set(false);
      },
    });
  }

  private loadMonths(): void {
    this.loading.set(true);
    this.api.list().subscribe({
      next: (months) => {
        this.months.set(months);
        this.loading.set(false);
      },
      error: () => {
        this.notify.error('Failed to load months');
        this.loading.set(false);
      },
    });
  }

  private loadCurrent(mode: PeriodMode): void {
    this.api.current(mode).subscribe({
      next: (result) => this.applyCurrent(result),
    });
  }

  private applyCurrent(current: CurrentOpenPeriod): void {
    this.startMonthId.set(current.startMonthId);
    this.endMonthId.set(current.endMonthId);
    this.form.controls.startMonthId.setValue(current.startMonthId, {
      emitEvent: false,
    });
    this.form.controls.endMonthId.setValue(current.endMonthId, {
      emitEvent: false,
    });
  }
}
